/**
 * DateTime Utilities
 *
 * Timezone-aware date/time formatting utilities for Vanguard Atlas.
 * Implements ADR-007 Timestamp & Timezone Strategy.
 *
 * Usage:
 *   - All API timestamps are in UTC (ISO 8601 with Z suffix)
 *   - Use these utilities to convert to user's timezone for display
 *   - User timezone is obtained from the application store
 */

import { formatDistanceToNow, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

/**
 * Default timezone when user preference is not available.
 */
export const DEFAULT_TIMEZONE = 'UTC';

/**
 * Get user's timezone from localStorage or return default.
 * In production, this should come from a store like Zustand.
 */
export function getUserTimezone(): string {
  // Try to get from localStorage as a fallback
  const stored = typeof window !== 'undefined'
    ? localStorage.getItem('atlas_user_timezone')
    : null;
  return stored || DEFAULT_TIMEZONE;
}

/**
 * Set user's timezone in localStorage.
 * Call this when user preferences are loaded.
 */
export function setUserTimezone(timezone: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('atlas_user_timezone', timezone);
  }
}

/**
 * Format a UTC timestamp to user's timezone.
 *
 * @param timestamp - ISO 8601 timestamp (e.g., "2026-01-19T15:30:00Z")
 * @param formatStr - date-fns format string
 * @param timezone - Optional timezone override
 * @returns Formatted date string in user's timezone
 *
 * @example
 * formatDateTime("2026-01-19T15:30:00Z")
 * // => "Jan 19, 2026, 3:30 PM" (if user is in UTC)
 *
 * formatDateTime("2026-01-19T15:30:00Z", "yyyy-MM-dd HH:mm", "America/New_York")
 * // => "2026-01-19 10:30"
 */
export function formatDateTime(
  timestamp: string | Date,
  formatStr: string = 'PPpp', // e.g., "Jan 19, 2026, 3:30 PM"
  timezone?: string
): string {
  const tz = timezone || getUserTimezone();
  const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;

  return formatInTimeZone(date, tz, formatStr);
}

/**
 * Format date only (no time).
 *
 * @param timestamp - ISO 8601 timestamp or Date
 * @param formatStr - date-fns format string for date
 * @param timezone - Optional timezone override
 * @returns Formatted date string
 *
 * @example
 * formatDate("2026-01-19T15:30:00Z")
 * // => "Jan 19, 2026"
 */
export function formatDate(
  timestamp: string | Date,
  formatStr: string = 'PP', // e.g., "Jan 19, 2026"
  timezone?: string
): string {
  return formatDateTime(timestamp, formatStr, timezone);
}

/**
 * Format time only.
 *
 * @param timestamp - ISO 8601 timestamp or Date
 * @param formatStr - date-fns format string for time
 * @param timezone - Optional timezone override
 * @returns Formatted time string
 *
 * @example
 * formatTime("2026-01-19T15:30:00Z")
 * // => "3:30 PM"
 */
export function formatTime(
  timestamp: string | Date,
  formatStr: string = 'p', // e.g., "3:30 PM"
  timezone?: string
): string {
  return formatDateTime(timestamp, formatStr, timezone);
}

/**
 * Format relative time (e.g., "5 minutes ago").
 *
 * Note: Relative time is calculated from current UTC time, not affected by timezone.
 * The timezone parameter is kept for API consistency but doesn't affect the calculation.
 *
 * @param timestamp - ISO 8601 timestamp or Date
 * @param _timezone - Optional timezone override (not used for relative time calculation)
 * @returns Relative time string
 *
 * @example
 * formatRelative("2026-01-19T15:25:00Z")
 * // => "5 minutes ago" (if current time is 15:30 UTC)
 */
export function formatRelative(
  timestamp: string | Date,
  _timezone?: string
): string {
  // For relative time, we compare UTC timestamps directly
  // The timezone doesn't affect "5 minutes ago" - that's absolute time difference
  const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
  return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Get current time in user's timezone.
 *
 * @param timezone - Optional timezone override
 * @returns Current Date in the specified timezone
 */
export function now(timezone?: string): Date {
  const tz = timezone || getUserTimezone();
  return toZonedTime(new Date(), tz);
}

/**
 * Parse an ISO timestamp and convert to user's timezone.
 *
 * @param timestamp - ISO 8601 timestamp string
 * @param timezone - Optional timezone override
 * @returns Date object in the specified timezone
 */
export function parseToTimezone(
  timestamp: string,
  timezone?: string
): Date {
  const tz = timezone || getUserTimezone();
  const date = parseISO(timestamp);
  return toZonedTime(date, tz);
}

/**
 * Format a timestamp for display based on user's format preferences.
 *
 * @param timestamp - ISO 8601 timestamp or Date
 * @param options - Format options
 * @returns Formatted string
 */
export function formatForDisplay(
  timestamp: string | Date,
  options: {
    dateFormat?: 'short' | 'medium' | 'long' | 'iso';
    timeFormat?: 'short' | 'medium' | 'iso' | '12h' | '24h';
    includeTime?: boolean;
    timezone?: string;
  } = {}
): string {
  const {
    dateFormat = 'medium',
    timeFormat = 'short',
    includeTime = true,
    timezone,
  } = options;

  const dateFormatStr = DATE_FORMATS[dateFormat];
  const timeFormatStr = TIME_FORMATS[timeFormat] || TIME_FORMATS.short;

  const formatStr = includeTime
    ? `${dateFormatStr} ${timeFormatStr}`
    : dateFormatStr;

  return formatDateTime(timestamp, formatStr, timezone);
}

// =============================================================================
// Common Format Patterns
// =============================================================================

/**
 * Common date format patterns.
 */
export const DATE_FORMATS = {
  short: 'MM/dd/yyyy',
  medium: 'PP',       // e.g., "Jan 19, 2026"
  long: 'PPPP',       // e.g., "Saturday, January 19th, 2026"
  iso: 'yyyy-MM-dd',
} as const;

/**
 * Common time format patterns.
 */
export const TIME_FORMATS = {
  short: 'p',         // e.g., "3:30 PM"
  medium: 'pp',       // e.g., "3:30:00 PM"
  iso: 'HH:mm:ss',
  '12h': 'h:mm a',    // e.g., "3:30 PM"
  '24h': 'HH:mm',     // e.g., "15:30"
} as const;

/**
 * Common datetime format patterns.
 */
export const DATETIME_FORMATS = {
  short: 'Pp',        // e.g., "01/19/2026, 3:30 PM"
  medium: 'PPp',      // e.g., "Jan 19, 2026, 3:30 PM"
  long: 'PPPPpppp',   // e.g., "Saturday, January 19th, 2026 at 3:30:00 PM GMT"
  iso: "yyyy-MM-dd'T'HH:mm:ssXXX",
} as const;

// =============================================================================
// Common Timezones for UI Selection
// =============================================================================

/**
 * Common timezones grouped by region for timezone picker.
 */
export const COMMON_TIMEZONES = {
  Americas: [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'America/Mexico_City',
    'America/Sao_Paulo',
    'America/Buenos_Aires',
  ],
  Europe: [
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Madrid',
    'Europe/Rome',
    'Europe/Amsterdam',
    'Europe/Moscow',
  ],
  'Asia/Pacific': [
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Hong_Kong',
    'Asia/Singapore',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Seoul',
    'Australia/Sydney',
    'Pacific/Auckland',
  ],
  Other: [
    'UTC',
    'Africa/Cairo',
    'Africa/Johannesburg',
  ],
} as const;

/**
 * Flatten timezone groups into a single array.
 */
export function getAllTimezones(): string[] {
  return Object.values(COMMON_TIMEZONES).flat();
}

/**
 * Format a timezone for display.
 *
 * @param timezone - IANA timezone string
 * @returns Human-readable timezone name
 *
 * @example
 * formatTimezoneForDisplay("America/New_York")
 * // => "America/New York"
 */
export function formatTimezoneForDisplay(timezone: string): string {
  return timezone.replace(/_/g, ' ');
}
